import type { DialogProps, SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';

export const F_SIMPLE_DIALOG_VARIANT = {
  SUCCESS: 'success',
  WARNING: 'warning',
} as const;

export type FSimpleDialogVariant =
  (typeof F_SIMPLE_DIALOG_VARIANT)[keyof typeof F_SIMPLE_DIALOG_VARIANT];

export type FSimpleDialogProps = {
  /**
   * Header content rendered next to the status icon. Accepts any node so callers
   * can pass plain copy or fully custom markup.
   */
  title: ReactNode;
  /**
   * Style overrides for the header text (merged onto the default). Use it to
   * tweak the title's font size, color, weight, etc. per dialog.
   */
  titleSx?: SxProps<Theme>;
  /**
   * Body content rendered below the header divider.
   */
  children: ReactNode;
  /**
   * Selects the leading status icon: a green check for success, a red triangle
   * for warning. Omit to render the header without an icon.
   */
  variant?: FSimpleDialogVariant;
  /**
   * Label for the primary action button. The button is only rendered when
   * `onAction` is provided, so a dialog can offer a cancel/close button alone.
   */
  actionText?: string;
  onAction?: () => void;
  /**
   * When provided, a secondary button with this label is rendered alongside the
   * primary action (as in the warning confirmation layout).
   */
  cancelText?: string;
  onCancel?: () => void;
  /**
   * Custom width for the dialog paper. Falls back to the responsive default;
   * the paper never shrinks below its minimum width.
   */
  width?: number | string;
  /**
   * Custom height for the dialog paper. Falls back to fitting the content;
   * the paper never shrinks below its minimum height.
   */
  height?: number | string;
  /**
   * When true, the body is replaced with a centered loader, both action
   * buttons are disabled, and the dialog cannot be dismissed.
   */
  loading?: boolean;
} & Omit<DialogProps, 'title'>;
